import { ShippingProviderType } from "@redotech/redo-fulfillment-carrier-common/shipment";
import { SENDCLOUD_CARRIER_LABELS } from "@redotech/sendcloud-carriers/carrier-display";

/**
 * Canonical Redo-provided return carriers, keyed by shipping provider.
 * Adding a ShippingProviderType fails to compile until it registers its rate.carrier strings.
 */
export interface RedoReturnProvidedCarrier {
  /** Verbatim `rate.carrier` the provider writes. */
  carrier: string;
  /** Merchant location-picker checkbox this carrier belongs to. */
  pickerGroup: RedoReturnCarrierPickerGroup;
  /** When set, this member is the persisted location-picker identity for the group. */
  canonical?: boolean;
}

export interface RedoReturnCarrierProviderRegistration {
  /** Carriers this integration quotes. Empty: dynamic names, or no Redo return rates. */
  carriers: readonly RedoReturnProvidedCarrier[];
}

/** Merchant-facing picker groups, in display order. Unknown group names are a type error. */
export const REDO_RETURN_CARRIER_PICKER_GROUPS = [
  "UPS",
  "UPS SurePost",
  "FedEx",
  "FedEx SmartPost",
  "USPS",
  "Canada Post",
  "Australia Post",
  "DHL eCommerce",
  "DHL Express",
  "Royal Mail",
  "Austrian Post",
  "bpost",
  "Chronopost",
  "Colissimo",
  "Correos",
  "Correos Express",
  "DHL",
  "DPD",
  "Evri",
  "GLS",
  "InPost",
  "Mondial Relay",
  "NZ Post",
  "Poste Italiane",
  "PostNL",
] as const;

export type RedoReturnCarrierPickerGroup =
  (typeof REDO_RETURN_CARRIER_PICKER_GROUPS)[number];

function provided(
  carrier: string,
  pickerGroup: RedoReturnCarrierPickerGroup,
  canonical?: boolean,
): RedoReturnProvidedCarrier {
  return canonical
    ? { carrier, pickerGroup, canonical }
    : { carrier, pickerGroup };
}

export const REDO_RETURN_CARRIER_PROVIDERS: Record<
  ShippingProviderType,
  RedoReturnCarrierProviderRegistration
> = {
  [ShippingProviderType.CANADA_POST]: {
    carriers: [provided("CanadaPost", "Canada Post")],
  },
  [ShippingProviderType.DX_FREIGHT]: { carriers: [] },
  [ShippingProviderType.EASYPOST]: {
    carriers: [
      provided("UPSSurePost", "UPS SurePost"),
      provided("FedEx", "FedEx"),
      provided("FedExDefault", "FedEx"),
      provided("FedExSmartPost", "FedEx SmartPost"),
      provided("USPSReturns", "USPS"),
      provided("AustraliaPost", "Australia Post"),
      provided("DhlEcs", "DHL eCommerce"),
      provided("DHLExpress", "DHL Express"),
      provided("RoyalMailV3", "Royal Mail"),
      // Canonical so "DPD" stays the stored picker identity WeGrow already used.
      provided("DPD", "DPD", true),
      // EasyPost DpdNlAccount quotes rate.carrier "DPDNL"; folds into the DPD picker group.
      provided("DPDNL", "DPD"),
      // DPD Local, which EasyPost lists under its new name, Interlink Express.
      provided("InterlinkExpress", "DPD"),
    ],
  },
  [ShippingProviderType.FEDEX_DIRECT]: {
    carriers: [
      provided("FedEx", "FedEx"),
      provided("FedExDefault", "FedEx"),
      provided("FedExSmartPost", "FedEx SmartPost"),
    ],
  },
  [ShippingProviderType.GLOBALE]: { carriers: [] },
  [ShippingProviderType.IDF_SHIPPING]: { carriers: [] },
  [ShippingProviderType.MOCK]: { carriers: [] },
  [ShippingProviderType.NZ_POST]: {
    carriers: [
      provided("PARCELPOST", "NZ Post"),
      // The label API spells it COURIERPOST; the rate API returns CourierPost.
      provided("COURIERPOST", "NZ Post"),
      provided("CourierPost", "NZ Post"),
    ],
  },
  [ShippingProviderType.SEKO]: { carriers: [] },
  [ShippingProviderType.SENDCLOUD]: {
    // Derived from the code→label map the provider stamps on rate.carrier, so
    // the two cannot drift. The picker group is the label itself.
    carriers: SENDCLOUD_CARRIER_LABELS.map((carrier) =>
      provided(carrier, carrier),
    ),
  },
  [ShippingProviderType.SOAPBOX]: { carriers: [] },
  [ShippingProviderType.UPS_DIRECT]: { carriers: [provided("UPS", "UPS")] },
  [ShippingProviderType.USPS_DIRECT]: {
    carriers: [provided("USPS", "USPS", true)],
  },
  [ShippingProviderType.WEGROW]: {
    carriers: [
      provided("DPD", "DPD"),
      provided("DHL", "DHL"),
      provided("PostNL", "PostNL"),
      provided("Chronopost", "Chronopost"),
      provided("bpost", "bpost"),
      provided("Austrian Post", "Austrian Post"),
      provided("Correos", "Correos"),
      provided("Poste Italiane", "Poste Italiane"),
    ],
  },
};

function allProvidedCarriers(): RedoReturnProvidedCarrier[] {
  return Object.values(REDO_RETURN_CARRIER_PROVIDERS).flatMap(
    (registration) => [...registration.carriers],
  );
}

/** Unique `rate.carrier` strings for a picker group, canonical member first. */
export function registeredCarriersInPickerGroup(
  group: RedoReturnCarrierPickerGroup,
): string[] {
  const seen = new Set<string>();
  const canonical: string[] = [];
  const rest: string[] = [];
  for (const entry of allProvidedCarriers()) {
    if (entry.pickerGroup !== group || seen.has(entry.carrier)) {
      continue;
    }
    seen.add(entry.carrier);
    if (entry.canonical) {
      canonical.push(entry.carrier);
    } else {
      rest.push(entry.carrier);
    }
  }
  return [...canonical, ...rest];
}

/** The picker group a provider's `rate.carrier` is registered under, or undefined when unregistered. */
export function getRedoReturnCarrierPickerGroup(
  carrier: string,
): RedoReturnCarrierPickerGroup | undefined {
  return allProvidedCarriers().find((entry) => entry.carrier === carrier)
    ?.pickerGroup;
}

/** Unique registered `rate.carrier` strings, in picker-group order. */
export const REGISTERED_REDO_RETURN_CARRIERS: readonly string[] =
  REDO_RETURN_CARRIER_PICKER_GROUPS.flatMap(registeredCarriersInPickerGroup);

/** True when this provider should be asked for rates given the current allowlist. */
export function providerOffersAllowedRedoCarrier(
  provider: ShippingProviderType,
  allowedCarriers: readonly string[],
): boolean {
  const providedCarriers = REDO_RETURN_CARRIER_PROVIDERS[provider].carriers;
  if (providedCarriers.length === 0) {
    return true;
  }
  const allowed = new Set(allowedCarriers);
  return providedCarriers.some((entry) => allowed.has(entry.carrier));
}
